import { Inventory, InventoryImage } from "types/inventory";
import { api } from "services/api.service";
import { Blob } from "activestorage";
import { uploadFile } from "hooks/useDirectUpload";
import { DriveFolderUpload, PhotoCamera } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Box, Typography } from "@mui/material";
import React, { useRef, useState, useCallback } from "react";
import { createRoot, Root } from "react-dom/client";
import Webcam from "react-webcam";

interface ActiveStorageBlob extends Blob {
  signed_id: string;
  filename: string;
  content_type: string;
}

const WebcamCapture = ({ onCapture, onCancel }: { onCapture: (file: File) => void; onCancel: () => void }) => {
  const webcamRef = useRef<Webcam>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>("");

  const handleDevices = useCallback(
    (mediaDevices: MediaDeviceInfo[]) => {
      const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
      setDevices(videoDevices);
      if (videoDevices.length > 0 && !deviceId) {
        setDeviceId(videoDevices[0].deviceId);
      }
    },
    [deviceId],
  );

  React.useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        fetch(imageSrc)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], `webcam-capture-${Date.now()}.jpg`, { type: "image/jpeg" });
            onCapture(file);
          });
      }
    }
  }, [webcamRef, onCapture]);

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="md">
      <DialogTitle>Take a Photo</DialogTitle>
      <DialogContent>
        <Box sx={{ width: 640, height: 480, position: "relative" }}>
          {devices.length > 0 ? (
            <Webcam
              audio={false}
              ref={webcamRef}
              videoConstraints={{ deviceId }}
              screenshotFormat="image/jpeg"
              width="100%"
              height="100%"
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f0f0f0",
              }}
            >
              <Typography>No camera detected</Typography>
            </Box>
          )}
        </Box>

        {devices.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Select Camera:
            </Typography>
            <Stack direction="row" spacing={1}>
              {devices.map((device, index) => (
                <Button
                  key={device.deviceId}
                  variant={deviceId === device.deviceId ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setDeviceId(device.deviceId)}
                >
                  Camera {index + 1}
                </Button>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={capture} variant="contained" color="primary" disabled={devices.length === 0}>
          Capture Photo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InventoryPhotoPickerModal = ({
  onFileSelected,
  onClose,
}: {
  onFileSelected: (file: File) => void;
  onClose: () => void;
}) => {
  const [useWebcam, setUseWebcam] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFileSelected(files[0]);
      onClose();
    }
  };

  const handleCapture = (file: File) => {
    onFileSelected(file);
    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose}>
      {useWebcam ? (
        <WebcamCapture onCapture={handleCapture} onCancel={() => setUseWebcam(false)} />
      ) : (
        <>
          <DialogTitle>Add Inventory Photo</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 300, pt: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={<DriveFolderUpload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
                size="large"
                sx={{ py: 2 }}
              >
                Choose from files
              </Button>

              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={() => setUseWebcam(true)}
                fullWidth
                size="large"
                sx={{ py: 2 }}
              >
                Use webcam
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export const openInventoryPhotoPickerWithWebcam = async (
  inventory: Inventory,
  onInventoryImageAdded: (inventoryImage: InventoryImage) => void,
): Promise<void> => {
  const modalContainer = document.createElement("div");
  document.body.appendChild(modalContainer);
  const root: Root = createRoot(modalContainer);

  const cleanupModal = () => {
    root.unmount();
    document.body.removeChild(modalContainer);
  };

  return new Promise<void>((resolve) => {
    const handleFileSelected = async (file: File) => {
      try {
        const blob = (await uploadFile(file, {
          service: "amazon_public",
          keyPrefix: `inventories/${inventory.uuid}`,
          useFilename: true,
        })) as ActiveStorageBlob;

        const body = {
          inventory_image: {
            inventory_id: inventory.id,
            url: blob.signed_id,
          },
        };

        const response = await api.post("/inventory_images", body);
        onInventoryImageAdded(response.data.inventoryImage);
      } catch (error) {
        console.error("Failed to upload image:", error);
      }
      resolve();
    };

    root.render(
      <InventoryPhotoPickerModal
        onFileSelected={handleFileSelected}
        onClose={() => {
          cleanupModal();
          resolve();
        }}
      />,
    );
  });
};

export default openInventoryPhotoPickerWithWebcam;
